import { useState } from 'react';
import PhoneInput from 'react-phone-number-input/input';
import styled from '@emotion/styled';
import {
  countryCodes,
  getCountryCallingCode,
  getCountryName,
} from './utils/countriesUtils';
import type { CountryCode } from 'libphonenumber-js/core';

const countrySelectWidth = '80px';

const Wrapper = styled.div`
  display: flex;
  height: 48px;
  margin: 10px 0px 0px 0px;
`;

const StyledPhoneInput = styled(PhoneInput)<{
  errorColor: string;
  hasError: boolean;
  placeholder?: string;
}>`
  width: calc(100% - ${countrySelectWidth}) !important;
  height: 100% !important;

  border-left: 0 !important;
  border-radius: 0 !important;
  padding: 10px 15px !important;
  border: 1px solid #d9d9d9;

  &::placeholder {
    color: #bfbfbf !important;
  }

  &:focus {
    outline: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: ${countrySelectWidth};
`;

const Select = styled.select`
  appearance: none;
  position: absolute;
  opacity: 0;
  width: 100% !important;
  height: 100% !important;
  border: 1px solid #d9d9d9;
  &:hover {
    cursor: pointer;
  }
`;

const ArrowDown = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #bfbfbf;
  margin-right: 6px;
`;

interface SelectButtonProps {
  errorColor?: string;
  hasError?: boolean;
  hovered?: boolean;
}

const SelectButton = styled.button<SelectButtonProps>`
  font-size: 16px;
  display: flex;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${(props) => (props.hasError ? props.errorColor : '#d9d9d9')};
  background-color: ${(props) =>
    props.hovered ? 'rgb(243, 243, 243)' : 'white'};
  padding: 10px;
`;

export interface PhoneNumberInputProps {
  ariaLabelledBy?: string;
  countryCode: CountryCode;
  dataTestId?: string;
  errorColor: string;
  hasError: boolean;
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
}

export const PhoneNumberInput = ({
  ariaLabelledBy,
  countryCode: cc,
  dataTestId,
  errorColor,
  hasError,
  phoneNumber,
  setPhoneNumber,
}: PhoneNumberInputProps) => {
  const [hovered, setHovered] = useState(false);
  const [countryCode, setCountryCode] = useState(cc);

  const onCountryChange = (value: CountryCode) => {
    setCountryCode(value);
    const callingCode = `+${getCountryCallingCode(countryCode)}`;
    if (phoneNumber && phoneNumber.indexOf(callingCode) === 0) {
      const newCallingCode = `+${getCountryCallingCode(value)}`;
      setPhoneNumber(`${newCallingCode}${phoneNumber.split(callingCode)[1]}`);
    }
  };

  return (
    <Wrapper>
      <SelectContainer>
        <SelectButton
          className="phone-input-button"
          errorColor={errorColor}
          hasError={hasError}
          hovered={hovered}
        >
          <ArrowDown />
          {`+${getCountryCallingCode(countryCode)}`}
        </SelectButton>

        <Select
          aria-label="country code"
          data-testid={dataTestId}
          onChange={(e) => onCountryChange(e.target.value as CountryCode)}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          value={countryCode}
        >
          {countryCodes.map((countryCode) => (
            <option key={countryCode} value={countryCode}>
              {getCountryName(countryCode)}{' '}
              {`+${getCountryCallingCode(countryCode)}`}
            </option>
          ))}
        </Select>
      </SelectContainer>
      <StyledPhoneInput
        aria-labelledby={ariaLabelledBy}
        country={countryCode as CountryCode}
        errorColor={errorColor}
        hasError={hasError}
        onChange={(val) => setPhoneNumber(val || '')}
        placeholder="Enter phone number"
        value={phoneNumber}
      />
    </Wrapper>
  );
};

export default PhoneNumberInput;
